import { useEffect, useRef, useState } from "react";
import { Download, Eye, Copy, ExternalLink, Link2, Check } from "lucide-react";

export interface ImageContextMenuProps {
  x: number;
  y: number;
  imageSrc: string;
  imageAlt?: string;
  onClose: () => void;
  onPreview: (src: string, name: string) => void;
  onDownload: (src: string, name: string) => void;
}

export default function ImageContextMenu({
  x,
  y,
  imageSrc,
  imageAlt = "imagen",
  onClose,
  onPreview,
  onDownload,
}: ImageContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [copiedStatus, setCopiedStatus] = useState<string | null>(null);

  const cleanName = (imageAlt || "imagen")
    .replace(/[^\w.-]/gi, "_")
    .replace(/^_+|_+$/g, "") || "imagen";
  const filename = cleanName.includes(".") ? cleanName : `${cleanName}.png`;

  // Cerrar al hacer clic fuera o presionar escape
  useEffect(() => {
    const handleDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  // Ajustar posición para que no se desborde de la pantalla
  const menuWidth = 220;
  const menuHeight = 230;
  const adjustedX = Math.min(x, window.innerWidth - menuWidth - 10);
  const adjustedY = Math.min(y, window.innerHeight - menuHeight - 10);

  const handleCopyImage = async () => {
    try {
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      let exportBlob = blob;
      const targetType = blob.type && blob.type.startsWith("image/") ? blob.type : "image/png";

      if (targetType !== blob.type || blob.type === "image/svg+xml") {
        const img = new Image();
        img.crossOrigin = "anonymous";
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageSrc;
        });
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          exportBlob = await new Promise<Blob>((resolve) =>
            canvas.toBlob((b) => resolve(b || blob), targetType !== "image/svg+xml" ? targetType : "image/png")
          );
        }
      }
      await navigator.clipboard.write([
        new ClipboardItem({ [exportBlob.type || "image/png"]: exportBlob }),
      ]);
      setCopiedStatus("image");
      setTimeout(() => onClose(), 800);
    } catch {
      // Fallback: copiar url
      try {
        await navigator.clipboard.writeText(imageSrc);
        setCopiedStatus("url");
        setTimeout(() => onClose(), 800);
      } catch {
        onClose();
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(imageSrc);
      setCopiedStatus("url");
      setTimeout(() => onClose(), 800);
    } catch {
      onClose();
    }
  };

  const handleOpenNewTab = () => {
    window.open(imageSrc, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <div
      ref={menuRef}
      style={{ top: `${adjustedY}px`, left: `${adjustedX}px` }}
      className="fixed z-50 min-w-[210px] bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-2xl py-1 select-none animate-fadeIn text-xs"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-1.5 border-b border-[var(--border-color)]/70 text-2xs font-semibold text-[var(--text-muted)] uppercase tracking-wider truncate">
        {filename}
      </div>

      <button
        onClick={() => {
          onDownload(imageSrc, filename);
          onClose();
        }}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[var(--text-primary)] hover:bg-accent/15 hover:text-accent transition-colors font-medium"
      >
        <Download size={14} className="flex-shrink-0 text-accent" />
        <span>Descargar imagen</span>
      </button>

      <button
        onClick={() => {
          onPreview(imageSrc, filename);
          onClose();
        }}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
      >
        <Eye size={14} className="flex-shrink-0 text-[var(--text-muted)]" />
        <span>Ver en pantalla completa</span>
      </button>

      <div className="h-px bg-[var(--border-color)]/60 my-1" />

      <button
        onClick={handleCopyImage}
        className="w-full flex items-center justify-between px-3 py-2 text-left text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <Copy size={14} className="flex-shrink-0 text-[var(--text-muted)]" />
          <span>Copiar imagen</span>
        </div>
        {copiedStatus === "image" && (
          <span className="flex items-center gap-0.5 text-2xs text-green-500 font-medium">
            <Check size={12} /> Copiado
          </span>
        )}
      </button>

      <button
        onClick={handleCopyLink}
        className="w-full flex items-center justify-between px-3 py-2 text-left text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <Link2 size={14} className="flex-shrink-0 text-[var(--text-muted)]" />
          <span>Copiar enlace de imagen</span>
        </div>
        {copiedStatus === "url" && (
          <span className="flex items-center gap-0.5 text-2xs text-green-500 font-medium">
            <Check size={12} /> Copiado
          </span>
        )}
      </button>

      <button
        onClick={handleOpenNewTab}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
      >
        <ExternalLink size={14} className="flex-shrink-0 text-[var(--text-muted)]" />
        <span>Abrir en nueva pestaña</span>
      </button>
    </div>
  );
}
